import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Organisation } from '../types/api';
import { organisationService } from '../services/api';

interface OrganisationSelectorProps {
  value?: number;
  onChange: (organisationId: number) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  organisations?: Organisation[];
}

export function OrganisationSelector({
  value,
  onChange,
  placeholder = "Sélectionner une organisation...",
  className = "",
  disabled = false,
  label,
  organisations: organisationsProp
}: OrganisationSelectorProps) {
  const [localOrganisations, setLocalOrganisations] = useState<Organisation[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Charger les organisations (utiliser la prop si fournie, sinon charger depuis l'API)
  useEffect(() => {
    if (organisationsProp && organisationsProp.length) {
      setLocalOrganisations(organisationsProp);
      return;
    }

    const loadOrganisations = async () => {
      setIsLoading(true);
      try {
        const data = await organisationService.getOrganisations();
        setLocalOrganisations(data);
      } catch (error) {
        console.error('Erreur lors du chargement des organisations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganisations();
  }, [organisationsProp]);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus sur le champ de recherche quand le dropdown s'ouvre
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Petit délai pour laisser l'animation se terminer
      setTimeout(() => searchInputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  // Filtrer les organisations selon le terme de recherche
  const filteredOrganisations = useMemo(() => {
    const source = organisationsProp ?? localOrganisations;
    if (!searchTerm.trim()) return source;

    return source.filter(org =>
      org.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (org.description && org.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [organisationsProp, localOrganisations, searchTerm]);

  // Organisation sélectionnée
  const selectedOrganisation = (organisationsProp ?? localOrganisations).find(org => org.id === value);

  const handleSelect = useCallback((organisation: Organisation) => {
    onChange(organisation.id);
    setIsOpen(false);
    setSearchTerm('');
  }, [onChange]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {label}
        </label>
      )}
      {/* Bouton de sélection */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-lg transition-all duration-200
          ${disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white hover:bg-gray-50 focus:bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
          }
          ${isOpen ? 'ring-2 ring-blue-200 border-blue-500' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <span className={`truncate ${selectedOrganisation ? 'text-gray-900' : 'text-gray-500'}`}>
            {selectedOrganisation ? selectedOrganisation.displayName : placeholder}
          </span>
          <motion.svg
            className="w-5 h-5 text-gray-400 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </motion.svg>
        </div>
      </button>

      {/* Menu déroulant */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden"
          >
            {/* Champ de recherche */}
            <div className="p-2 border-b border-gray-100">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Rechercher une organisation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
              />
            </div>

            {/* Liste des organisations */}
            <div className="max-h-48 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  Chargement...
                </div>
              ) : filteredOrganisations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm.trim() ? 'Aucune organisation trouvée' : 'Aucune organisation disponible'}
                </div>
              ) : (
                filteredOrganisations.map((organisation) => (
                  <motion.button
                    key={organisation.id}
                    type="button"
                    onClick={() => handleSelect(organisation)}
                    className={`
                      w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150
                      ${organisation.id === value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}
                    `}
                    whileHover={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.1 }}
                  >
                    <div className="font-medium">{organisation.displayName}</div>
                    {organisation.description && (
                      <div className="text-sm text-gray-500 mt-1">{organisation.description}</div>
                    )}
                  </motion.button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}